import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import StatusBadge from '../../../../components/StatusBadge';
import Spinner from '../../../../components/Spinner';
import MasterService, { StatusMasterFormPayload, StatusMasterType } from '../../../../services/master.service';
import { Modules } from '../../../../enums';

interface StatusMasterFormProps {
  type: StatusMasterType;
  routePath: string;
  hasActiveState: boolean;
}

const DEFAULT_COLOUR = '#4CAF50';
const HEX_COLOUR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const initialForm: StatusMasterFormPayload = {
  status_code: '',
  status_name: '',
  colour_code: DEFAULT_COLOUR,
  is_active_state: false,
  display_order: 0,
  notes: '',
};

const StatusMasterForm: React.FC<StatusMasterFormProps> = ({ type, routePath, hasActiveState }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<StatusMasterFormPayload>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    MasterService.get(type, id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          status_code: row.status_code ?? '',
          status_name: row.status_name ?? '',
          colour_code: row.colour_code ?? DEFAULT_COLOUR,
          is_active_state: row.is_active_state ?? false,
          display_order: row.display_order ?? 0,
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id, type]);

  const validate = useCallback(
    (data: StatusMasterFormPayload) => {
      const nextErrors: Record<string, string> = {};
      if (!data.status_code.trim()) {
        nextErrors.status_code = t('masters.statuses.form.pageTexts.fields.statusCode.errors.required');
      }
      if (!data.status_name.trim()) {
        nextErrors.status_name = t('masters.statuses.form.pageTexts.fields.statusName.errors.required');
      }
      if (!HEX_COLOUR_REGEX.test(data.colour_code)) {
        nextErrors.colour_code = t('masters.statuses.form.pageTexts.fields.colourCode.errors.invalid');
      }
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'is_active_state' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: StatusMasterFormPayload = {
          ...formData,
          display_order: Number(formData.display_order) || 0,
        };
        if (!hasActiveState) delete payload.is_active_state;

        if (edit && id) {
          await MasterService.update(type, id, payload);
          toast.success(<Toast message={t('masters.statuses.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await MasterService.create(type, payload);
          toast.success(<Toast message={t('masters.statuses.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${routePath}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, type, hasActiveState, navigate, routePath, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit
          ? t('masters.statuses.form.pageTexts.editTitle', { type: t(`masters.statuses.types.${type}`) })
          : t('masters.statuses.form.pageTexts.addTitle', { type: t(`masters.statuses.types.${type}`) })}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="status_code"
            name="status_code"
            label={t('masters.statuses.form.pageTexts.fields.statusCode.label')}
            placeholder={t('masters.statuses.form.pageTexts.fields.statusCode.placeholder')}
            required
            value={formData.status_code}
            error={!!errors.status_code}
            errorMessage={errors.status_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="status_name"
            name="status_name"
            label={t('masters.statuses.form.pageTexts.fields.statusName.label')}
            placeholder={t('masters.statuses.form.pageTexts.fields.statusName.placeholder')}
            required
            value={formData.status_name}
            error={!!errors.status_name}
            errorMessage={errors.status_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <label className="form-label d-block">{t('masters.statuses.form.pageTexts.fields.colourCode.label')}</label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="color"
              value={HEX_COLOUR_REGEX.test(formData.colour_code) ? formData.colour_code : DEFAULT_COLOUR}
              onChange={(e) => setFormData((prev) => ({ ...prev, colour_code: e.target.value }))}
            />
            <Input
              id="colour_code"
              name="colour_code"
              placeholder={t('masters.statuses.form.pageTexts.fields.colourCode.placeholder')}
              value={formData.colour_code}
              error={!!errors.colour_code}
              errorMessage={errors.colour_code}
              onChange={handleChange}
            />
            <StatusBadge label={formData.status_name || formData.status_code || '—'} colour={formData.colour_code} />
          </div>
        </div>
        {hasActiveState && (
          <div className="col-md-4">
            <CustomizedSwitch
              label={t('masters.statuses.form.pageTexts.fields.isActiveState.label')}
              name="is_active_state"
              checked={!!formData.is_active_state}
              onChange={handleChange}
            />
          </div>
        )}
        <div className="col-md-4">
          <Input
            id="display_order"
            name="display_order"
            type="number"
            min="0"
            label={t('masters.statuses.form.pageTexts.fields.displayOrder.label')}
            placeholder={t('masters.statuses.form.pageTexts.fields.displayOrder.placeholder')}
            value={formData.display_order}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.statuses.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.statuses.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.statuses.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${routePath}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.statuses.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default StatusMasterForm;
